/**
 * TT-03 — who may see the review page and who may lock.
 *
 * Mirrors the BE's role sets (contract design-note TT-D9) so the UI hides what
 * the server would refuse. **The server is the security boundary** — this hook
 * only decides what to render, and every mutation is still gated server-side.
 *
 * Roles are read fresh from `/me` rather than from the persisted session blob:
 * the session's `user.roles` is a boot-time snapshot, and a role change made
 * while the tab is open would otherwise leave a back-office user staring at a
 * page they now do have access to (the same reason `useFreshRoles` exists for
 * the sites dialogs). While the fetch is in flight the seeded roles decide, so
 * the page does not flash a permission warning at somebody who has permission.
 */
import { useFreshRoles } from "../../sites/shared/useFreshRoles.js";

/** Read all trackings, lock, export. Matches `TIME_TRACKING_MANAGER_ROLES`. */
const REVIEW_ROLES = [
  "project_manager",
  "tenant_admin",
  "back_office",
  "platform_admin",
] as const;

/**
 * Write the commercial config. Matches `TIME_TRACKING_CONFIG_WRITE_ROLES`.
 *
 * `back_office` was added 2026-08-18 (time-tracking/v1 1.1.0) — the people who
 * review and invoice standby are the same people who maintain what it is worth.
 * Identical to REVIEW_ROLES today; kept separate because the two are free to
 * diverge again.
 */
const CONFIG_WRITE_ROLES = [
  "project_manager",
  "tenant_admin",
  "back_office",
  "platform_admin",
] as const;

export interface TimeTrackingReviewAccess {
  /** May open the review page at all. */
  canReview: boolean;
  /** May bulk-lock a selection. Same set as `canReview` in v1. */
  canLock: boolean;
  /** May PUT the per-project commercial config. */
  canWriteConfig: boolean;
  /** True until the fresh `/me` has answered. */
  isPending: boolean;
}

export function useTimeTrackingReviewAccess(): TimeTrackingReviewAccess {
  const roles = useFreshRoles();
  const has = (set: readonly string[]): boolean =>
    roles.some((r) => set.includes(r));
  return {
    canReview: has(REVIEW_ROLES),
    canLock: has(REVIEW_ROLES),
    canWriteConfig: has(CONFIG_WRITE_ROLES),
    // `useFreshRoles` seeds from the session synchronously, so there is never a
    // genuinely unknown state to guard — the flag exists so a future async
    // source can be swapped in without touching every call site.
    isPending: false,
  };
}
